import { useApolloClient } from "@apollo/client";
import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { OrderDetailsWithMetadataDocument } from "@dashboard/graphql";
import { getMutationState } from "@dashboard/misc";
import { Box, Button, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import CegidDraftReview from "./CegidDraftReview";
import CegidInvoiceLineRow from "./CegidInvoiceLineRow";
import {
  useCegidCustomerByNifQuery,
  useCegidInvoiceDraftCreateMutation,
  useCegidInvoiceDraftDiscardMutation,
  useCegidInvoiceDraftFinalizeMutation,
  useCegidInvoiceDraftQuery,
  useCegidInvoicePreviewQuery,
} from "./graphql";
import { autoMergeLines, CegidLineUnit, lineKey, sharedFields, toLineUnits } from "./mergeGroups";
import { cegidInvoiceDialogMessages as messages } from "./messages";
import {
  CegidCustomer,
  CegidDocumentType,
  CegidErrorFragment,
  CegidInvoiceDraft,
  CegidInvoiceFormLine,
  CegidInvoiceLine,
  CegidInvoiceLineInput,
} from "./types";

interface CegidInvoiceDialogProps {
  open: boolean;
  orderId: string;
  onClose: () => void;
  onCompleted: () => void;
}

const toFormLine = (line: CegidInvoiceLine): CegidInvoiceFormLine => ({
  ...line,
  applyToWholeProduct: line.scope === "PRODUCT",
  createIfMissing: false,
  extraText: "",
  label: line.itemDescription || line.itemCode || "",
  mergeGroup: null,
});

const normalizeNif = (value: string) => value.replace(/\s/g, "");

const formatCegidAddress = (customer: CegidCustomer) =>
  [customer.addressDetail, [customer.postcode, customer.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

const ErrorList = ({ errors }: { errors: CegidErrorFragment[] }) => (
  <>
    {errors.map((error, index) => (
      <Text color="critical1" key={index}>
        {error.message}
      </Text>
    ))}
  </>
);

const CegidInvoiceDialog = ({ open, orderId, onClose, onCompleted }: CegidInvoiceDialogProps) => {
  const intl = useIntl();
  const apolloClient = useApolloClient();
  const [fetchPreview, previewResult] = useCegidInvoicePreviewQuery();
  const [fetchDraft, draftResult] = useCegidInvoiceDraftQuery();
  const [createDraft, createResult] = useCegidInvoiceDraftCreateMutation();
  const [finalizeDraft, finalizeResult] = useCegidInvoiceDraftFinalizeMutation();
  const [discardDraft, discardResult] = useCegidInvoiceDraftDiscardMutation();
  const [fetchCustomer, customerResult] = useCegidCustomerByNifQuery();

  const [documentType, setDocumentType] = useState<CegidDocumentType>("FR");
  const [paymentMechanism, setPaymentMechanism] = useState("CC");
  const [nif, setNif] = useState("");
  const [customerName, setCustomerName] = useState("");
  // CEGID's record for the NIF (undefined: not looked up / not a full NIF).
  const [cegidCustomer, setCegidCustomer] = useState<CegidCustomer | null | undefined>(undefined);
  const [lines, setLines] = useState<CegidInvoiceFormLine[]>([]);
  // Keys of the rows (lines or merged groups) ticked for merging.
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [mergeCount, setMergeCount] = useState(0);
  const [draft, setDraft] = useState<CegidInvoiceDraft | null>(null);
  const [reviewed, setReviewed] = useState(false);

  const preview = previewResult.data?.cegidInvoicePreview;

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraft(null);
    setReviewed(false);
    fetchPreview({ variables: { orderId } });
  }, [open, orderId, fetchPreview]);

  useEffect(() => {
    if (!preview) {
      return;
    }

    setDocumentType(preview.documentType);
    setPaymentMechanism(preview.paymentMechanism);
    setNif(preview.customerTaxRegistrationNumber);
    setCustomerName(preview.customerBusinessName);
    setCegidCustomer(
      preview.customerTaxRegistrationNumber ? (preview.cegidCustomer ?? null) : undefined,
    );
    setLines(autoMergeLines(preview.lines.map(toFormLine)));
    setSelectedUnits([]);

    if (preview.hasDraft) {
      fetchDraft({ variables: { orderId } });
    }
  }, [preview, fetchDraft, orderId]);

  useEffect(() => {
    if (draftResult.data?.cegidInvoiceDraft) {
      setDraft(draftResult.data.cegidInvoiceDraft);
      setReviewed(false);
    }
  }, [draftResult.data]);

  useEffect(() => {
    const lookedUp = customerResult.variables?.taxRegistrationNumber;

    // Ignore answers for a NIF that has since been edited away.
    if (!customerResult.data || !lookedUp || lookedUp !== normalizeNif(nif)) {
      return;
    }

    const customer = customerResult.data.cegidCustomerByNif;

    setCegidCustomer(customer);

    if (customer?.businessName) {
      setCustomerName(customer.businessName);
    }
    // Only a new lookup result should prefill the name, not every NIF keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerResult.data, customerResult.variables]);

  const handleNifChange = (value: string) => {
    const normalized = normalizeNif(value);

    setNif(value);
    setCegidCustomer(undefined);

    if (/^\d{9}$/.test(normalized)) {
      fetchCustomer({ variables: { taxRegistrationNumber: normalized } });
    }
  };

  const customerNameHelperText = (() => {
    if (customerResult.loading) {
      return intl.formatMessage(messages.customerLookupLoading);
    }

    if (cegidCustomer) {
      return intl.formatMessage(messages.customerFromCegid, {
        address: formatCegidAddress(cegidCustomer) || cegidCustomer.businessName,
      });
    }

    if (cegidCustomer === null) {
      return intl.formatMessage(messages.customerNotInCegid);
    }

    return undefined;
  })();

  const patchLines = (
    keys: string[],
    patch: (line: CegidInvoiceFormLine) => Partial<CegidInvoiceFormLine>,
  ) => {
    setLines(prev =>
      prev.map(line => (keys.includes(lineKey(line)) ? { ...line, ...patch(line) } : line)),
    );
  };

  const units = toLineUnits(lines);

  const handleMergeSelected = () => {
    const selected = units.filter(unit => selectedUnits.includes(unit.key));
    const [target] = selected[0].lines;
    // Reuse a selected group's id, so it keeps its place in the list.
    const groupId =
      selected.map(unit => unit.lines[0].mergeGroup).find(Boolean) ?? `manual:${mergeCount}`;

    setMergeCount(count => count + 1);
    patchLines(
      selected.flatMap(unit => unit.lines.map(lineKey)),
      () => ({ ...sharedFields(target), mergeGroup: groupId }),
    );
    setSelectedUnits([]);
  };

  const handleUnmerge = (unit: CegidLineUnit) => {
    const originals = new Map(
      (preview?.lines ?? []).map(line => [lineKey(line), toFormLine(line)]),
    );

    // Back to each line's own SKU and name, as prefilled.
    patchLines(unit.lines.map(lineKey), line => {
      const original = originals.get(lineKey(line));

      return { ...(original ? sharedFields(original) : {}), mergeGroup: null };
    });
    setSelectedUnits(prev => prev.filter(key => key !== unit.key));
  };

  const handleUnitSelected = (unit: CegidLineUnit, selected: boolean) => {
    setSelectedUnits(prev =>
      selected ? [...prev, unit.key] : prev.filter(key => key !== unit.key),
    );
  };

  const selectedUnitCount = units.filter(unit => selectedUnits.includes(unit.key)).length;
  // Selecting rows only makes sense with at least two rows that can be merged.
  const canMerge = units.filter(unit => !unit.lines[0].isShipping).length >= 2;
  const hasMergedGroup = units.some(unit => unit.lines[0].mergeGroup);

  const documentTypeOptions: Option[] = useMemo(
    () => [
      { value: "FR", label: intl.formatMessage(messages.documentTypeFr) },
      { value: "FT", label: intl.formatMessage(messages.documentTypeFt) },
    ],
    [intl],
  );
  const paymentMechanismOptions: Option[] = useMemo(
    () =>
      (
        [
          ["CC", messages.paymentCC],
          ["DC", messages.paymentDC],
          ["MB", messages.paymentMB],
          ["DE", messages.paymentDE],
          ["TR", messages.paymentTR],
          ["MO", messages.paymentMO],
          ["OU", messages.paymentOU],
        ] as const
      ).map(([value, message]) => ({ value, label: intl.formatMessage(message) })),
    [intl],
  );

  const handleCreateDraft = async () => {
    const lineInputs: CegidInvoiceLineInput[] = lines.map(line => ({
      orderLineId: line.isShipping ? undefined : line.orderLineId,
      itemCode: line.itemCode ?? "",
      itemDescription: line.itemDescription,
      scope: line.applyToWholeProduct ? "PRODUCT" : "VARIANT",
      createIfMissing: !line.isShipping && !line.existsInCegid && line.createIfMissing,
      extraText: line.extraText.trim() || undefined,
      mergeGroup: (!line.isShipping && line.mergeGroup) || undefined,
    }));

    const result = await createDraft({
      variables: {
        orderId,
        lines: lineInputs,
        documentType,
        paymentMechanism: documentType === "FR" ? paymentMechanism : undefined,
        customerTaxRegistrationNumber: nif || undefined,
        customerBusinessName: customerName || undefined,
      },
    });
    const created = result.data?.cegidInvoiceDraftCreate.draft;

    if (created) {
      setDraft(created);
      setReviewed(false);
    }
  };

  const handleFinalize = async () => {
    if (!draft) {
      return;
    }

    const result = await finalizeDraft({
      variables: { orderId, documentId: draft.documentId },
    });

    if (
      result.data?.cegidInvoiceDraftFinalize.invoice &&
      !result.data.cegidInvoiceDraftFinalize.errors.length
    ) {
      // Finalizing moves the order on (invoice step done, "Preparação" next):
      // reload it so the stepper and invoice list reflect that right away.
      await apolloClient.refetchQueries({ include: [OrderDetailsWithMetadataDocument] });
      onCompleted();
    } else {
      // The draft was re-checked server side; show its current state.
      setReviewed(false);
      fetchDraft({ variables: { orderId } });
    }
  };

  const handleDiscard = async () => {
    const result = await discardDraft({ variables: { orderId } });

    if (!result.data?.cegidInvoiceDraftDiscard.errors.length) {
      setDraft(null);
      setReviewed(false);
      fetchPreview({ variables: { orderId } });
    }
  };

  const createErrors = createResult.data?.cegidInvoiceDraftCreate.errors ?? [];
  const finalizeErrors = finalizeResult.data?.cegidInvoiceDraftFinalize.errors ?? [];
  const discardErrors = discardResult.data?.cegidInvoiceDraftDiscard.errors ?? [];
  const queryError = previewResult.error ?? draftResult.error;
  const frBlocked = documentType === "FR" && preview && !preview.isPaid;
  const isLoading = previewResult.loading || draftResult.loading;

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="lg" __maxHeight="90vh" overflowY="auto">
        <DashboardModal.Header>
          {intl.formatMessage(draft ? messages.reviewTitle : messages.title)}
        </DashboardModal.Header>

        {queryError ? (
          <Text color="critical1">
            {queryError.message || intl.formatMessage(messages.notConfigured)}
          </Text>
        ) : draft ? (
          <Box display="grid" gap={3}>
            <CegidDraftReview draft={draft} reviewed={reviewed} onReviewedChange={setReviewed} />
            <ErrorList errors={[...finalizeErrors, ...discardErrors]} />
          </Box>
        ) : preview ? (
          <Box display="grid" gap={4}>
            <Box
              backgroundColor={preview.isPaid ? "success1" : "critical1"}
              padding={2}
              borderRadius={3}
            >
              <Text fontWeight="medium" color={preview.isPaid ? "success1" : "critical1"}>
                {intl.formatMessage(preview.isPaid ? messages.orderPaid : messages.orderNotPaid, {
                  total: `${preview.orderTotal.toFixed(2)} €`,
                })}
              </Text>
            </Box>

            <Box display="grid" __gridTemplateColumns="1fr 1fr 1fr 1fr" gap={3}>
              <Select
                options={documentTypeOptions}
                label={intl.formatMessage(messages.documentTypeLabel)}
                name="documentType"
                value={documentType}
                onChange={value => setDocumentType(value as CegidDocumentType)}
              />
              {documentType === "FR" ? (
                <Select
                  options={paymentMechanismOptions}
                  label={intl.formatMessage(messages.paymentMechanismLabel)}
                  name="paymentMechanism"
                  value={paymentMechanism}
                  onChange={value => setPaymentMechanism(value as string)}
                />
              ) : (
                <Box />
              )}
              <Input
                name="customerTaxRegistrationNumber"
                label={intl.formatMessage(messages.nifLabel)}
                helperText={intl.formatMessage(messages.nifHelperText)}
                value={nif}
                onChange={event => handleNifChange(event.target.value)}
              />
              <Input
                name="customerBusinessName"
                label={intl.formatMessage(messages.customerNameLabel)}
                helperText={customerNameHelperText}
                value={customerName}
                onChange={event => setCustomerName(event.target.value)}
              />
            </Box>

            {frBlocked && <Text color="critical1">{intl.formatMessage(messages.frNeedsPaid)}</Text>}

            <Box display="grid" gap={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
                <Text fontWeight="bold">{intl.formatMessage(messages.linesHeader)}</Text>
                {canMerge && (
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={selectedUnitCount < 2}
                    onClick={handleMergeSelected}
                    data-test-id="cegid-merge-selected"
                  >
                    {intl.formatMessage(messages.mergeSelected, { count: selectedUnitCount })}
                  </Button>
                )}
              </Box>
              {(canMerge || hasMergedGroup) && (
                <Text size={2} color="default2">
                  {intl.formatMessage(canMerge ? messages.mergeHint : messages.mergeAutoHint)}
                </Text>
              )}
              {units.map(unit => {
                const [first] = unit.lines;
                const keys = unit.lines.map(lineKey);

                return (
                  <CegidInvoiceLineRow
                    key={unit.key}
                    line={first}
                    members={first.mergeGroup ? unit.lines : undefined}
                    selected={selectedUnits.includes(unit.key)}
                    onSelectedChange={
                      canMerge && !first.isShipping
                        ? selected => handleUnitSelected(unit, selected)
                        : undefined
                    }
                    onUnmerge={() => handleUnmerge(unit)}
                    onChange={patch => patchLines(keys, () => patch)}
                  />
                );
              })}
            </Box>

            <ErrorList errors={createErrors} />
            <Text size={2} color="default2">
              {intl.formatMessage(messages.createDraftHint)}
            </Text>
          </Box>
        ) : null}

        <DashboardModal.Actions>
          {draft ? (
            <>
              <Button
                variant="error"
                onClick={handleDiscard}
                disabled={discardResult.loading || finalizeResult.loading}
                data-test-id="cegid-discard-draft"
              >
                {intl.formatMessage(messages.discardDraft)}
              </Button>
              <BackButton onClick={onClose}>{intl.formatMessage(messages.close)}</BackButton>
              <ConfirmButton
                transitionState={getMutationState(
                  finalizeResult.called,
                  finalizeResult.loading,
                  finalizeErrors,
                )}
                disabled={!draft.canFinalize || !reviewed || isLoading}
                onClick={handleFinalize}
                data-test-id="cegid-finalize"
              >
                {intl.formatMessage(messages.finalize)}
              </ConfirmButton>
            </>
          ) : (
            <>
              <BackButton onClick={onClose} />
              <ConfirmButton
                transitionState={getMutationState(
                  createResult.called,
                  createResult.loading,
                  createErrors,
                )}
                disabled={isLoading || !lines.length || !!frBlocked || !!queryError}
                onClick={handleCreateDraft}
                data-test-id="cegid-create-draft"
              >
                {intl.formatMessage(messages.createDraft)}
              </ConfirmButton>
            </>
          )}
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

CegidInvoiceDialog.displayName = "CegidInvoiceDialog";
export default CegidInvoiceDialog;
